import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

type AuthState = {
  authenticated: boolean;
  authRequired: boolean | null;
  loading: boolean;
};

type AuthContextValue = AuthState & {
  login: (password: string) => Promise<boolean>;
  logout: () => void;
};

const KEY = "ocr_access_password";

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    authenticated: false,
    authRequired: null,
    loading: true,
  });

  useEffect(() => {
    const stored = localStorage.getItem(KEY);

    // ヘルスチェックで auth_required を確認
    fetch("/api/health")
      .then((r) => r.json() as Promise<{ auth_required: boolean }>)
      .then((data) => {
        if (!data.auth_required) {
          setState({ authenticated: true, authRequired: false, loading: false });
          return;
        }
        if (stored) {
          // 既に保存済みのパスワードを検証
          verifyPassword(stored)
            .then((ok) => {
              if (!ok) localStorage.removeItem(KEY);
              setState({
                authenticated: ok,
                authRequired: true,
                loading: false,
              });
            })
            .catch(() => {
              setState({
                authenticated: false,
                authRequired: true,
                loading: false,
              });
            });
        } else {
          setState({
            authenticated: false,
            authRequired: true,
            loading: false,
          });
        }
      })
      .catch(() => {
        setState({
          authenticated: false,
          authRequired: true,
          loading: false,
        });
      });
  }, []);

  const login = async (password: string): Promise<boolean> => {
    const ok = await verifyPassword(password);
    if (ok) {
      localStorage.setItem(KEY, password);
      setState((s) => ({ ...s, authenticated: true }));
    }
    return ok;
  };

  const logout = () => {
    localStorage.removeItem(KEY);
    setState((s) => ({ ...s, authenticated: false }));
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

async function verifyPassword(password: string): Promise<boolean> {
  try {
    const res = await fetch("/api/auth/check", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function getStoredPassword(): string | null {
  return localStorage.getItem(KEY);
}
